// Renovation / Build Costs panel — mirrors src/components/RenoPanel.jsx.
//
// The panel scaffolds its full DOM once during construction and stores
// refs to every input, slider, readout, and label as instance fields.
// render() then just updates those values + toggles mode-specific
// visibility — the sliders are never destroyed, so their drag tracking
// survives the re-render loop and linked math (contingency amount,
// total cost, deal summary) updates live during a slider drag, the
// same way the Acquisition reduction slider already does.

import {
	computeDealMetrics,
	deriveBuildCostPerSqft,
	deriveBuildCostTotal,
	distributeByWeight,
	sumLineItems,
} from '../calculations';
import {IDealState} from '../defaults';
import {currencyInput, numInput, panelSection} from '../primitives';

export interface IRenoPanelHost {
	getState(): IDealState;
	update(patch: Partial<IDealState>): void;
}

export class RenoPanel {
	private rootEl: HTMLElement | null;
	private titleEl: HTMLElement | null = null;
	private subtitleEl: HTMLElement | null = null;
	private bodyEl: HTMLElement | null = null;
	private renoExpanded: boolean = true;

	// Build mode — property specs (sqft/beds/baths) wrapper + inputs
	private buildSpecsRow: HTMLElement | null = null;
	private buildSqftInput: HTMLInputElement | null = null;
	private buildBedsInput: HTMLInputElement | null = null;
	private buildBathsInput: HTMLInputElement | null = null;

	// Build mode — cost calculator wrapper + controls
	private buildBlock: HTMLElement | null = null;
	private buildPerSqftInput: HTMLInputElement | null = null;
	private buildTotalInput: HTMLInputElement | null = null;
	private buildSlider: HTMLInputElement | null = null;
	private buildSliderReadout: HTMLElement | null = null;
	private buildSqftHint: HTMLElement | null = null;

	// Reno mode — top-line input
	private renoTopLineWrap: HTMLElement | null = null;
	private renoTopLineInput: HTMLInputElement | null = null;

	// Itemized breakdown — toggle + grid (grid is rebuilt on item change)
	private breakdownWrap: HTMLElement | null = null;
	private breakdownToggle: HTMLButtonElement | null = null;
	private breakdownGrid: HTMLElement | null = null;

	// Reno mode — value-add leverage block
	private leverageBlock: HTMLElement | null = null;
	private encloseSqftInput: HTMLInputElement | null = null;
	private encloseCostInput: HTMLInputElement | null = null;
	private encloseValueInput: HTMLInputElement | null = null;
	private encloseValueLabelText: HTMLElement | null = null;
	private encloseValueLinkedBadge: HTMLElement | null = null;
	private encloseAddedCostEl: HTMLElement | null = null;
	private encloseAddedValueEl: HTMLElement | null = null;
	private encloseNetEl: HTMLElement | null = null;
	private encloseIncludeCheckbox: HTMLInputElement | null = null;
	private marketingSlider: HTMLInputElement | null = null;
	private marketingReadout: HTMLElement | null = null;
	private marketingCaption: HTMLElement | null = null;

	// Contingency
	private contingencyAmountEl: HTMLElement | null = null;
	private contingencySlider: HTMLInputElement | null = null;
	private contingencyReadout: HTMLElement | null = null;
	private contingencyHint: HTMLElement | null = null;

	// Footer
	private footerLabel: HTMLElement | null = null;
	private footerValue: HTMLElement | null = null;

	constructor(private host: IRenoPanelHost) {
		this.rootEl = document.getElementById('id_lb-deal-panel-reno');
		if (!this.rootEl) {
			return;
		}
		this.scaffoldShell();
		this.scaffoldBody();
		this.renoExpanded = this.host.getState().mode !== 'build';
		this.render();
	}

	render(): void {
		if (!this.bodyEl) {
			return;
		}
		const state = this.host.getState();
		const mode = state.mode || 'reno';
		const m = computeDealMetrics(state);

		if (this.titleEl) {
			this.titleEl.textContent = mode === 'build' ? 'Build Costs' : 'Renovation';
		}
		if (this.subtitleEl) {
			this.subtitleEl.textContent = mode === 'build'
				? 'Construction cost build-up + contingency'
				: 'Top-line total syncs with itemized breakdown';
		}

		// Mode-specific block visibility
		if (this.buildSpecsRow) {
			this.buildSpecsRow.style.display = mode === 'build' ? '' : 'none';
		}
		if (this.buildBlock) {
			this.buildBlock.style.display = mode === 'build' ? '' : 'none';
		}
		if (this.renoTopLineWrap) {
			this.renoTopLineWrap.style.display = mode === 'build' ? 'none' : '';
		}
		if (this.leverageBlock) {
			this.leverageBlock.style.display = mode === 'build' ? 'none' : '';
		}

		// Build specs
		this.setInputValue(this.buildSqftInput, state.property.sqft || 0);
		this.setInputValue(this.buildBedsInput, state.property.beds || 0);
		this.setInputValue(this.buildBathsInput, state.property.baths || 0);

		// Build calculator
		this.setInputValue(this.buildPerSqftInput, state.buildCostPerSqft || 0);
		this.setInputValue(this.buildTotalInput, state.renoTotal || 0);
		const sliderVal = Math.min(2000, Math.max(100, state.buildCostPerSqft || 400));
		if (this.buildSlider && document.activeElement !== this.buildSlider) {
			this.buildSlider.value = String(sliderVal);
		}
		if (this.buildSliderReadout) {
			this.buildSliderReadout.textContent = `$${(state.buildCostPerSqft || 0).toLocaleString()}/sqft`;
		}
		if (this.buildSqftHint) {
			this.buildSqftHint.style.display = (state.property.sqft || 0) === 0 ? '' : 'none';
		}

		// Reno top-line
		this.setInputValue(this.renoTopLineInput, state.renoTotal || 0);

		// Itemized breakdown — rebuild grid items (focus restore handles it)
		this.renderBreakdown(state);

		// Leverage — show/hide linked badge, update inputs and stats
		this.setInputValue(this.encloseSqftInput, state.enclosedSqft || 0);
		this.setInputValue(this.encloseCostInput, state.enclosedCostPerSqft || 0);
		this.setInputValue(this.encloseValueInput, state.enclosedValuePerSqft || 0);
		const showLinkedBadge = mode !== 'build' && !!state.valueSyncedToPPSF && (state.enclosedSqft || 0) > 0;
		if (this.encloseValueLinkedBadge) {
			this.encloseValueLinkedBadge.style.display = showLinkedBadge ? '' : 'none';
		}
		if (this.encloseAddedCostEl) {
			this.encloseAddedCostEl.textContent = formatCurrency(m.enclosedAddedCost);
		}
		if (this.encloseAddedValueEl) {
			this.encloseAddedValueEl.textContent = formatCurrency(m.enclosedAddedValue);
		}
		if (this.encloseNetEl) {
			if ((state.enclosedSqft || 0) > 0) {
				const netVal = m.enclosedAddedValue - m.enclosedAddedCost;
				const netPct = m.enclosedAddedCost > 0
					? ((m.enclosedAddedValue / m.enclosedAddedCost - 1) * 100).toFixed(0)
					: '0';
				this.encloseNetEl.textContent = `Net leverage: ${formatCurrency(netVal)} (${netPct}% over cost)`;
				this.encloseNetEl.style.display = '';
			} else {
				this.encloseNetEl.style.display = 'none';
			}
		}
		if (this.encloseIncludeCheckbox) {
			this.encloseIncludeCheckbox.checked = !!state.enclosedCostInReno;
		}

		// Marketing slider + readout + caption
		if (this.marketingSlider && document.activeElement !== this.marketingSlider) {
			this.marketingSlider.value = String(state.marketingMultiplier || 0);
		}
		if (this.marketingReadout) {
			this.marketingReadout.textContent = `+${(state.marketingMultiplier || 0).toFixed(1)}%`;
		}
		if (this.marketingCaption) {
			this.marketingCaption.textContent = `ARV bump: ${formatCurrency(m.arvWithEnclosed * (state.marketingMultiplier || 0) / 100)}`;
		}

		// Contingency
		if (this.contingencyAmountEl) {
			this.contingencyAmountEl.textContent = formatCurrency(m.contingencyAmount);
		}
		if (this.contingencySlider && document.activeElement !== this.contingencySlider) {
			this.contingencySlider.value = String(state.contingencyPct || 0);
		}
		if (this.contingencyReadout) {
			this.contingencyReadout.textContent = `${state.contingencyPct || 0}%`;
		}
		if (this.contingencyHint) {
			this.contingencyHint.textContent = mode === 'build'
				? '10–15% standard, 15–20% for complex/custom designs.'
				: 'Industry standard is 10–15% for cosmetic, 15–25% for full gut.';
		}

		// Footer
		if (this.footerLabel) {
			this.footerLabel.textContent = mode === 'build'
				? 'Total build cost (incl. contingency)'
				: 'Total renovation cost (incl. leverage + contingency)';
		}
		if (this.footerValue) {
			this.footerValue.textContent = formatCurrency(m.totalRenoCost);
		}
	}

	private setInputValue(input: HTMLInputElement | null, value: number): void {
		if (!input || document.activeElement === input) {
			return;
		}
		input.value = value > 0 ? Number(value).toLocaleString('en-US') : '';
	}

	private scaffoldShell(): void {
		if (!this.rootEl) {
			return;
		}
		const initialMode = this.host.getState().mode || 'reno';
		const handles = panelSection({
			rootEl: this.rootEl,
			icon: initialMode === 'build' ? 'construction' : 'build',
			title: initialMode === 'build' ? 'Build Costs' : 'Renovation',
		});
		this.titleEl = handles.titleEl;
		this.subtitleEl = handles.subtitleEl;
		this.bodyEl = handles.bodyEl;
	}

	private scaffoldBody(): void {
		if (!this.bodyEl) {
			return;
		}
		this.bodyEl.appendChild(this.scaffoldBuildSpecsRow());
		this.bodyEl.appendChild(this.scaffoldBuildBlock());
		this.bodyEl.appendChild(this.scaffoldRenoTopLine());
		this.bodyEl.appendChild(this.scaffoldBreakdown());
		this.bodyEl.appendChild(this.scaffoldLeverage());
		this.bodyEl.appendChild(this.scaffoldContingency());
		this.bodyEl.appendChild(this.scaffoldFooter());
	}

	// ─── Build mode: spec row ───
	private scaffoldBuildSpecsRow(): HTMLElement {
		const grid = document.createElement('div');
		grid.className = 'lb-deal-grid lb-deal-grid--3';
		this.buildSpecsRow = grid;

		const sqftField = this.makeNumField('Sqft');
		this.buildSqftInput = sqftField.input;
		this.buildSqftInput.addEventListener('input', () => {
			this.handleBuildSqftChange(this.parseNum(this.buildSqftInput!.value));
			this.render();
		});
		grid.appendChild(sqftField.wrap);

		const bedsField = this.makeNumField('Beds');
		this.buildBedsInput = bedsField.input;
		this.buildBedsInput.addEventListener('input', () => {
			const v = this.parseNum(this.buildBedsInput!.value);
			const state = this.host.getState();
			this.host.update({property: {...state.property, beds: v}});
			this.render();
		});
		grid.appendChild(bedsField.wrap);

		const bathsField = this.makeNumField('Baths');
		this.buildBathsInput = bathsField.input;
		this.buildBathsInput.addEventListener('input', () => {
			const v = this.parseNum(this.buildBathsInput!.value);
			const state = this.host.getState();
			this.host.update({property: {...state.property, baths: v}});
			this.render();
		});
		grid.appendChild(bathsField.wrap);

		return grid;
	}

	// ─── Build mode: cost calculator ───
	private scaffoldBuildBlock(): HTMLElement {
		const block = document.createElement('div');
		block.className = 'lb-deal-build-block';
		this.buildBlock = block;

		const grid = document.createElement('div');
		grid.className = 'lb-deal-grid lb-deal-grid--2';

		const perSqftField = this.makeCurrencyField('Cost / sqft', false);
		this.buildPerSqftInput = perSqftField.input;
		this.buildPerSqftInput.addEventListener('input', () => {
			this.handlePerSqftChange(this.parseNum(this.buildPerSqftInput!.value));
			this.render();
		});
		grid.appendChild(perSqftField.wrap);

		const totalField = this.makeCurrencyField('Total build cost', true);
		this.buildTotalInput = totalField.input;
		this.buildTotalInput.addEventListener('input', () => {
			this.handleBuildTotalChange(this.parseNum(this.buildTotalInput!.value));
			this.render();
		});
		grid.appendChild(totalField.wrap);

		block.appendChild(grid);

		const sliderRow = document.createElement('div');
		sliderRow.className = 'lb-deal-build-block__slider-row';
		const slider = document.createElement('input');
		slider.type = 'range';
		slider.min = '100';
		slider.max = '2000';
		slider.step = '5';
		slider.className = 'lb-deal-build-block__slider';
		this.buildSlider = slider;
		const sliderReadout = document.createElement('div');
		sliderReadout.className = 'lb-deal-build-block__readout';
		this.buildSliderReadout = sliderReadout;
		slider.addEventListener('input', () => {
			this.handlePerSqftChange(parseInt(slider.value, 10) || 0);
			this.render();
		});
		sliderRow.appendChild(slider);
		sliderRow.appendChild(sliderReadout);
		block.appendChild(sliderRow);

		const tickRow = document.createElement('div');
		tickRow.className = 'lb-deal-build-block__ticks';
		['$100 · basic spec', '$1,000 · upscale', '$2,000 · custom'].forEach(text => {
			const tick = document.createElement('span');
			tick.textContent = text;
			tickRow.appendChild(tick);
		});
		block.appendChild(tickRow);

		const hint = document.createElement('p');
		hint.className = 'lb-deal-build-block__hint';
		hint.textContent = 'Enter sqft above to derive a total from cost per sqft.';
		this.buildSqftHint = hint;
		block.appendChild(hint);

		return block;
	}

	// ─── Reno mode: top-line input ───
	private scaffoldRenoTopLine(): HTMLElement {
		const wrap = document.createElement('div');
		wrap.className = 'lb-deal-field';
		this.renoTopLineWrap = wrap;
		const label = document.createElement('label');
		label.className = 'lb-deal-field__label';
		label.textContent = 'Total renovation cost (top-line)';
		wrap.appendChild(label);
		const fieldWrap = currencyInput({
			value: 0,
			size: 'large',
			focusKey: 'reno-total',
			onChange: v => {
				const state = this.host.getState();
				const next = Math.max(0, v);
				this.host.update({
					renoTotal: next,
					renoItems: distributeByWeight(next, state.renoItems || []),
				});
				this.render();
			},
		});
		this.renoTopLineInput = fieldWrap.querySelector('input');
		wrap.appendChild(fieldWrap);
		return wrap;
	}

	// ─── Itemized breakdown ───
	private scaffoldBreakdown(): HTMLElement {
		const wrap = document.createElement('div');
		wrap.className = 'lb-deal-breakdown';
		this.breakdownWrap = wrap;
		const toggle = document.createElement('button');
		toggle.type = 'button';
		toggle.className = 'lb-deal-breakdown__toggle';
		this.breakdownToggle = toggle;
		toggle.addEventListener('click', () => {
			this.renoExpanded = !this.renoExpanded;
			this.render();
		});
		wrap.appendChild(toggle);
		const grid = document.createElement('div');
		grid.className = 'lb-deal-breakdown__grid';
		this.breakdownGrid = grid;
		wrap.appendChild(grid);
		return wrap;
	}

	private renderBreakdown(state: IDealState): void {
		if (!this.breakdownToggle || !this.breakdownGrid) {
			return;
		}
		this.breakdownToggle.textContent =
			`${this.renoExpanded ? '▾' : '▸'} Itemized breakdown (${(state.renoItems || []).length} categories)`;
		this.breakdownGrid.style.display = this.renoExpanded ? '' : 'none';
		while (this.breakdownGrid.firstChild) {
			this.breakdownGrid.removeChild(this.breakdownGrid.firstChild);
		}
		(state.renoItems || []).forEach((item, idx) => {
			const row = document.createElement('div');
			row.className = 'lb-deal-line-row';
			const label = document.createElement('span');
			label.className = 'lb-deal-line-row__label';
			label.textContent = item.label;
			row.appendChild(label);
			row.appendChild(currencyInput({
				value: item.amount || 0,
				size: 'small',
				focusKey: `reno-item-${item.id}`,
				onChange: v => {
					const next = (state.renoItems || []).map((i, i2) =>
						i2 === idx ? {...i, amount: Math.max(0, v)} : i,
					);
					this.host.update({
						renoItems: next,
						renoTotal: sumLineItems(next),
					});
					this.render();
				},
			}));
			this.breakdownGrid!.appendChild(row);
		});
	}

	// ─── Reno mode: value-add leverage ───
	private scaffoldLeverage(): HTMLElement {
		const block = document.createElement('div');
		block.className = 'lb-deal-leverage';
		this.leverageBlock = block;

		const head = document.createElement('div');
		head.className = 'lb-deal-leverage__head';
		const title = document.createElement('h4');
		title.textContent = 'Value-Add Leverage';
		head.appendChild(title);
		const sub = document.createElement('p');
		sub.className = 'lb-deal-leverage__subtitle';
		sub.textContent = 'Multipliers experienced flippers use to expand margin without scope creep.';
		head.appendChild(sub);
		block.appendChild(head);

		// Enclose-under-roof card
		const enclose = document.createElement('div');
		enclose.className = 'lb-deal-leverage__card';
		const encloseTitle = document.createElement('div');
		encloseTitle.className = 'lb-deal-leverage__card-title';
		encloseTitle.textContent = 'Enclose areas already under roof';
		enclose.appendChild(encloseTitle);
		const encloseHint = document.createElement('div');
		encloseHint.className = 'lb-deal-leverage__card-hint';
		encloseHint.textContent =
			'Convert porches/garages → heated sqft. Construction cost is typically far below the value of finished heated space.';
		enclose.appendChild(encloseHint);

		const encloseGrid = document.createElement('div');
		encloseGrid.className = 'lb-deal-grid lb-deal-grid--3 lb-deal-grid--dark';

		// Added sqft
		const sqftField = this.makeDarkNumField('Added sqft');
		this.encloseSqftInput = sqftField.input;
		this.encloseSqftInput.addEventListener('input', () => {
			this.host.update({enclosedSqft: this.parseNum(this.encloseSqftInput!.value)});
			this.render();
		});
		encloseGrid.appendChild(sqftField.wrap);

		// Cost / sqft
		const costField = this.makeDarkNumField('Cost / sqft to convert');
		this.encloseCostInput = costField.input;
		this.encloseCostInput.addEventListener('input', () => {
			this.host.update({enclosedCostPerSqft: this.parseNum(this.encloseCostInput!.value)});
			this.render();
		});
		encloseGrid.appendChild(costField.wrap);

		// Value / sqft (with optional linked badge)
		const valueField = this.makeDarkNumField('Value / sqft (heated)');
		this.encloseValueInput = valueField.input;
		this.encloseValueLabelText = valueField.labelText;
		this.encloseValueLinkedBadge = document.createElement('span');
		this.encloseValueLinkedBadge.className = 'lb-deal-badge lb-deal-badge--linked-amber';
		this.encloseValueLinkedBadge.textContent = 'linked';
		this.encloseValueLinkedBadge.style.display = 'none';
		valueField.label.appendChild(this.encloseValueLinkedBadge);
		this.encloseValueInput.addEventListener('input', () => {
			const v = this.parseNum(this.encloseValueInput!.value);
			const state = this.host.getState();
			const updates: Partial<IDealState> = {enclosedValuePerSqft: v};
			// React-equivalent behavior: when the link is active, edits
			// here propagate back to baseARV (and the ARV panel's PPSF
			// readout updates via render() → its recomputePPSFFromState).
			if (state.valueSyncedToPPSF) {
				const sqft = state.property.sqft || 0;
				if (sqft > 0) {
					updates.baseARV = Math.round(v * sqft);
				}
			}
			this.host.update(updates);
			this.render();
		});
		encloseGrid.appendChild(valueField.wrap);

		enclose.appendChild(encloseGrid);

		// Stats
		const stats = document.createElement('div');
		stats.className = 'lb-deal-leverage__stats';
		const addedCost = this.makeStatTile('Added cost', false);
		this.encloseAddedCostEl = addedCost.value;
		stats.appendChild(addedCost.tile);
		const addedValue = this.makeStatTile('Added ARV value', true);
		this.encloseAddedValueEl = addedValue.value;
		stats.appendChild(addedValue.tile);
		enclose.appendChild(stats);

		// Net leverage
		const net = document.createElement('div');
		net.className = 'lb-deal-leverage__net';
		this.encloseNetEl = net;
		enclose.appendChild(net);

		// Include checkbox
		const includeWrap = document.createElement('label');
		includeWrap.className = 'lb-deal-leverage__include';
		const include = document.createElement('input');
		include.type = 'checkbox';
		this.encloseIncludeCheckbox = include;
		include.addEventListener('change', () => {
			this.host.update({enclosedCostInReno: include.checked});
			this.render();
		});
		includeWrap.appendChild(include);
		const includeLabel = document.createElement('span');
		includeLabel.textContent = 'Add this cost to renovation total';
		includeWrap.appendChild(includeLabel);
		enclose.appendChild(includeWrap);

		block.appendChild(enclose);

		// Marketing multiplier card
		const marketing = document.createElement('div');
		marketing.className = 'lb-deal-leverage__card';
		const marketingTitle = document.createElement('div');
		marketingTitle.className = 'lb-deal-leverage__card-title';
		marketingTitle.textContent = 'Marketing Leverage Multiplier';
		marketing.appendChild(marketingTitle);
		const marketingHint = document.createElement('div');
		marketingHint.className = 'lb-deal-leverage__card-hint';
		marketingHint.textContent =
			'% bump on ARV when reno surfaces undermarketed features (e.g., views, character, finishes).';
		marketing.appendChild(marketingHint);

		const marketingRow = document.createElement('div');
		marketingRow.className = 'lb-deal-leverage__slider-row';
		const marketingSlider = document.createElement('input');
		marketingSlider.type = 'range';
		marketingSlider.min = '0';
		marketingSlider.max = '15';
		marketingSlider.step = '0.5';
		marketingSlider.className = 'lb-deal-leverage__slider';
		this.marketingSlider = marketingSlider;
		const marketingReadout = document.createElement('span');
		marketingReadout.className = 'lb-deal-leverage__readout';
		this.marketingReadout = marketingReadout;
		marketingSlider.addEventListener('input', () => {
			const v = Math.max(0, Math.min(15, parseFloat(marketingSlider.value) || 0));
			this.host.update({marketingMultiplier: v});
			this.render();
		});
		marketingRow.appendChild(marketingSlider);
		marketingRow.appendChild(marketingReadout);
		marketing.appendChild(marketingRow);

		const arvBump = document.createElement('div');
		arvBump.className = 'lb-deal-leverage__caption';
		this.marketingCaption = arvBump;
		marketing.appendChild(arvBump);

		block.appendChild(marketing);
		return block;
	}

	// ─── Contingency ───
	private scaffoldContingency(): HTMLElement {
		const wrap = document.createElement('div');
		wrap.className = 'lb-deal-contingency';
		const head = document.createElement('div');
		head.className = 'lb-deal-contingency__head';
		const title = document.createElement('span');
		title.className = 'lb-deal-contingency__title';
		title.textContent = 'Contingency';
		head.appendChild(title);
		const amount = document.createElement('span');
		amount.className = 'lb-deal-contingency__amount';
		this.contingencyAmountEl = amount;
		head.appendChild(amount);
		wrap.appendChild(head);

		const sliderRow = document.createElement('div');
		sliderRow.className = 'lb-deal-contingency__slider-row';
		const slider = document.createElement('input');
		slider.type = 'range';
		slider.min = '0';
		slider.max = '25';
		slider.step = '1';
		slider.className = 'lb-deal-contingency__slider';
		this.contingencySlider = slider;
		const readout = document.createElement('span');
		readout.className = 'lb-deal-contingency__readout';
		this.contingencyReadout = readout;
		slider.addEventListener('input', () => {
			const v = Math.max(0, Math.min(25, parseInt(slider.value, 10) || 0));
			this.host.update({contingencyPct: v});
			this.render();
		});
		sliderRow.appendChild(slider);
		sliderRow.appendChild(readout);
		wrap.appendChild(sliderRow);

		const hint = document.createElement('p');
		hint.className = 'lb-deal-contingency__hint';
		this.contingencyHint = hint;
		wrap.appendChild(hint);
		return wrap;
	}

	// ─── Footer ───
	private scaffoldFooter(): HTMLElement {
		const row = document.createElement('div');
		row.className = 'lb-deal-line-row lb-deal-line-row--summary';
		const label = document.createElement('span');
		label.className = 'lb-deal-line-row__label';
		this.footerLabel = label;
		row.appendChild(label);
		const value = document.createElement('span');
		value.className = 'lb-deal-line-row__value';
		this.footerValue = value;
		row.appendChild(value);
		return row;
	}

	// Build-mode synced edit handlers — keep buildCostPerSqft × sqft = renoTotal
	// invariant whenever any of the three inputs change.
	private handlePerSqftChange(perSqft: number): void {
		const state = this.host.getState();
		const sqft = state.property.sqft || 0;
		if (sqft > 0) {
			const total = deriveBuildCostTotal(perSqft, sqft);
			this.host.update({
				buildCostPerSqft: perSqft,
				buildCostTotal: total,
				renoTotal: total,
				renoItems: distributeByWeight(total, state.renoItems || []),
			});
		} else {
			this.host.update({buildCostPerSqft: perSqft});
		}
	}

	private handleBuildTotalChange(total: number): void {
		const state = this.host.getState();
		const sqft = state.property.sqft || 0;
		const patch: Partial<IDealState> = {
			buildCostTotal: total,
			renoTotal: total,
			renoItems: distributeByWeight(total, state.renoItems || []),
		};
		if (sqft > 0) {
			patch.buildCostPerSqft = deriveBuildCostPerSqft(total, sqft);
		}
		this.host.update(patch);
	}

	private handleBuildSqftChange(sqft: number): void {
		const state = this.host.getState();
		const perSqft = state.buildCostPerSqft || 0;
		const patch: Partial<IDealState> = {
			property: {...state.property, sqft},
		};
		if (sqft > 0 && perSqft > 0) {
			const total = deriveBuildCostTotal(perSqft, sqft);
			patch.buildCostTotal = total;
			patch.renoTotal = total;
			patch.renoItems = distributeByWeight(total, state.renoItems || []);
		}
		this.host.update(patch);
	}

	private parseNum(raw: string): number {
		const cleaned = raw.replace(/[^\d.]/g, '');
		const parsed = parseFloat(cleaned);
		return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
	}

	// Field factories that return both the wrap and the inner input so
	// callers can store stable refs to the inputs.

	private makeNumField(label: string): {wrap: HTMLElement; input: HTMLInputElement} {
		const wrap = document.createElement('div');
		wrap.className = 'lb-deal-field';
		const lbl = document.createElement('label');
		lbl.className = 'lb-deal-field__label';
		lbl.textContent = label;
		wrap.appendChild(lbl);
		const input = numInput({value: 0, onChange: () => {}});
		wrap.appendChild(input);
		return {wrap, input};
	}

	private makeCurrencyField(label: string, large: boolean): {wrap: HTMLElement; input: HTMLInputElement} {
		const wrap = document.createElement('div');
		wrap.className = 'lb-deal-field';
		const lbl = document.createElement('label');
		lbl.className = 'lb-deal-field__label';
		lbl.textContent = label;
		wrap.appendChild(lbl);
		const fieldWrap = currencyInput({value: 0, size: large ? 'large' : 'default', onChange: () => {}});
		wrap.appendChild(fieldWrap);
		const input = fieldWrap.querySelector('input') as HTMLInputElement;
		return {wrap, input};
	}

	private makeDarkNumField(label: string): {wrap: HTMLElement; label: HTMLLabelElement; labelText: HTMLElement; input: HTMLInputElement} {
		const wrap = document.createElement('div');
		wrap.className = 'lb-deal-field lb-deal-field--dark';
		const lbl = document.createElement('label');
		lbl.className = 'lb-deal-field__label';
		const labelText = document.createElement('span');
		labelText.textContent = label;
		lbl.appendChild(labelText);
		wrap.appendChild(lbl);
		const input = numInput({value: 0, dark: true, onChange: () => {}});
		wrap.appendChild(input);
		return {wrap, label: lbl, labelText, input};
	}

	private makeStatTile(label: string, accent: boolean): {tile: HTMLElement; value: HTMLElement} {
		const tile = document.createElement('div');
		tile.className = `lb-deal-stat${accent ? ' lb-deal-stat--accent' : ''}`;
		const lbl = document.createElement('div');
		lbl.className = 'lb-deal-stat__label';
		lbl.textContent = label;
		tile.appendChild(lbl);
		const value = document.createElement('div');
		value.className = 'lb-deal-stat__value';
		tile.appendChild(value);
		return {tile, value};
	}
}

function formatCurrency(n: number): string {
	if (!Number.isFinite(n)) {
		return '$0';
	}
	return n.toLocaleString('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0});
}
